import { ol_Feature, ol_geom } from "@omnia/exports/openlayers"
import { convertGeometryToWkt, FieldAreaType, FieldsMap, FieldViewModel, ICropping, IField, IOmniaSettings, IOmniaShell, isUndefined, map } from "../../omnia"
import { commands } from "../../strings"
import { FieldDependencies } from "../../types"
import { BoundaryDrawingContext } from "../boundary/drawingContext"

/// <reference path="../boundary/drawingContext.ts" />

const cmd = commands

export class FieldDrawingContext extends BoundaryDrawingContext {

    private overlaps: FieldViewModel[];
    public _autoCropOnCommit: boolean;

    private _status: FieldDependencies


    constructor(
        target: IField,
        private targetStatusProvider: (id: number) => ng.IPromise<FieldDependencies>,
        targetAreaTypeProvider: () => FieldAreaType,
        map: FieldsMap,
        topo: omnia.map.IMapTopologyService,
        private shell: IOmniaShell,
        switchToMapView: () => void,
        settings: IOmniaSettings) {

        super(map, topo, switchToMapView, map.fieldsLayer, settings, target?.BoundaryArea, {
            copy: a => {

                const t = targetAreaTypeProvider()

                if (t == FieldAreaType.WorkingArea) {
                    target.WorkingArea = a
                }
                else {
                    target.WholeArea = a
                }

            },
            get enabled() {
                return targetAreaTypeProvider() != FieldAreaType.BoundaryArea
            }
        })

        this._autoCropOnCommit = this.settings.get("autoCropOnCommit");

        this.setupTarget(false, target);

    }

    startDrawing() {

        if (!this._status) {
            this.getTargetStatus()
        }

        return super.startDrawing()

    }

    private getTargetStatus() {

        const id = this.target.FieldId

        if (!id) {
            this._status = null
            this._allowDeletion = false
            return
        }

        this.targetStatusProvider(id).then(s => {
            this._status = s
            this._allowDeletion = true
        })

    }

    getEditCommandText(): string {
        return this.newBoundary ? cmd.drawFieldBoundary : cmd.editFieldBoundary
    }

    protected updateFeatureGeometry(feature: ol_Feature): boolean {

        if (this._autoCropOnCommit) {

            let conflicts = this.getConflictingFields(feature.getGeometry());

            if ((!conflicts.contained.length) && conflicts.overlapping.length) {
                return this.autoCropInternal(feature, conflicts.overlapping);
            }

        }

    }

    protected validateInternal(feature: ol_Feature): boolean {

        var fieldGeometry = feature.getGeometry();

        if (!this.validateOverlaps(fieldGeometry)) {
            return false;
        }

        if (!this.validateCrops(fieldGeometry)) {
            return false;
        }

        return true;

    }


    private validateCrops(fieldGeometry: any): boolean {

        var crops = this.checkCropsOutsideOfField(fieldGeometry);

        if (crops.length) {
            const msgFieldOutsideCropping = "Invalid field boundary: some of its cropping lies outside of the boundary"
            this.addValidationMessage(msgFieldOutsideCropping)
            return false;
        }

        return true;

    }

    private checkCropsOutsideOfField(fieldGeometry: any): ICropping[] {

        var field = <IField>this.target;
        if (!field.Cropping) return [];

        var crops = field.Cropping.filter(c => !!c.Boundary).map(c => {

            var boundary;

            boundary = convertGeometryToWkt(c.Boundary);

            return {
                crop: c,
                boundary: boundary,
                intersection: this.topo.intersection(fieldGeometry, boundary),
            }

        });

        crops = crops
            .filter(i => !!i.intersection)
            .filter(i => !(i.intersection.getOpenLayersGeometry()));

        return crops.map(i => i.crop);


    }

    private validateOverlaps(fieldGeometry: ol_geom.Geometry): boolean {

        this.overlaps = null;

        let conflicts = this.getConflictingFields(fieldGeometry);

        if (conflicts.contained.length) {
            const other = conflicts.contained[0].getDescription()
            const msg = `Invalid boundary: it contains that of <em>${other}</em>`
            this.addValidationMessage(msg);
            return false;
        }

        this.overlaps = (conflicts.overlapping.length ? conflicts.overlapping : null);

        if (this.overlaps) {
            const other = this.overlaps[0].getDescription()
            const msg = `The boundary overlaps that of <em>${other}</em>. You can use the <b>Auto crop</b> button below to crop the boundary automatically.`;
            this.addValidationMessage(msg);
            return false;
        }

        return true;


    }

    private getConflictingFields(fieldGeometry: ol_geom.Geometry): {
        overlapping: FieldViewModel[]
        contained: FieldViewModel[]
    } {


        const run = () => {

            const fieldId = this.target.FieldId;

            const features = this.map.fieldsLayer.getFeatures();

            const conflicts = features.map(other => {

                let data = <FieldViewModel>other.get("data");
                let otherGeometry = other.getGeometry();

                if ((data.fieldId != fieldId) && (!shouldIgnoreBoundary(data)) && (!this.topo.disjoint(otherGeometry, fieldGeometry))) {

                    if (this.topo.contains(fieldGeometry, otherGeometry)) {

                        return {
                            feature: other,
                            data: data,
                            relation: 1
                        }

                    }
                    else if (!this.topo.touches(otherGeometry, fieldGeometry)) {

                        const i = this.topo.intersection(otherGeometry, fieldGeometry, true)

                        if (i && (i.getArea() > 0.01)) {

                            return {
                                feature: other,
                                data: data,
                                relation: 2,
                            }
                        }
                    }

                }


            }).filter(i => !!i);

            return {
                overlapping: conflicts.filter(c => c.relation == 2).map(i => i.data),
                contained: conflicts.filter(c => c.relation == 1).map(i => i.data),
            };

        }

        try {
            return run()
        }
        catch (error) {

            console.error(error)

            return {
                overlapping: [],
                contained: []
            }

        }
    }


    autoCropOnCommit(value: boolean): boolean {

        if (!isUndefined(value)) {
            this._autoCropOnCommit = value;
            this.settings.set("autoCropOnCommit", true);
        }

        return this._autoCropOnCommit;

    }

    supportsAutoCrop(): boolean {
        return true;
    }

    canAutoCrop(): boolean {
        return this.overlaps && (this.overlaps.length > 0);
    }

    autoCrop(): boolean {

        if (!(this.feature && this.overlaps)) return false;

        if (this.autoCropInternal(this.feature, this.overlaps)) {
            return this.validate();
        }

        return false;

    }

    private autoCropInternal(feature: ol_Feature, overlaps: FieldViewModel[]): boolean {

        if (!(overlaps && (overlaps.length > 0))) {
            return false;
        }

        let geometry = feature.getGeometry();

        let others = this.map.findFeaturesWithData(overlaps).map(f => f.getGeometry());

        let diff = this.topo.difference(geometry, others).getOpenLayersGeometry();

        let results = omnia.map.getPolygonsAsArray([diff]);

        if (results.length > 1) {

            let sortedPolygons = results
                .map(i => {
                    return {
                        item: i,
                        area: i.getArea()
                    }
                })
                .sort((i1, i2) => i2.area - i1.area);

            diff = sortedPolygons[0].item;

        }

        if (diff) {

            feature.setGeometry(diff);

            return true;

        }

    }

    delete() {

        const msgCannotFieldDeleteBoundary = "The boundary cannot be deleted because there are layers linked to this field"
        const msgCannotFieldDeleteBoundaryNzRiskMap = "The boundary cannot be deleted because there are NVZ risk map features on or near this field, affecting its manure spreadable area"

        let error = null

        if (this._status && this._status.HasLayers) {
            error = msgCannotFieldDeleteBoundary
        }
        else if (this.target.ManureSpreadAreaCalc) {
            error = msgCannotFieldDeleteBoundaryNzRiskMap
        }

        if (error) {
            omnia.shell.showWarning(this.shell, error)
            return false
        }

        return super.delete()

    }

    hasNewUncroppedAreas() {

        if (this._deleted) {
            return false
        }

        if (!(this.hasChanged && this.editCount)) {
            return false
        }

        if (!(this.initialGeometry && this.feature)) {
            return false
        }

        try {
            const diff = this.topo.difference(this.feature.getGeometry(), [this.initialGeometry])
            return diff && diff.isValid() && (diff.getDimension() == 2) && (diff.getArea() >= 10)
        }
        catch (e) {
            console.error(e)
            return false
        }


    }

}

function shouldIgnoreBoundary(field: FieldViewModel) {
    return field && field.field.IgnoreBoundary && field.disabled
}
